import { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import {
	Pencil,
	Trash2,
	Plus,
	Search,
        Filter,
        Building2,
	Receipt,
	CreditCard,
	Users,
        Activity,
	CircleCheck,
	Clock,
	TriangleAlert,
	Pause,
	Ban,
        HardDrive,
        ShieldAlert,
        TrendingUp,
        Wifi,
        MessageSquare,
        CheckCheck,
        Wrench,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import { Switch } from "../../components/ui/switch";
import {
        Select,
        SelectContent,
        SelectItem,
        SelectTrigger,
        SelectValue,
} from "../../components/ui/select";
import {
        Dialog,
        DialogContent,
        DialogDescription,
        DialogHeader,
        DialogTitle,
} from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { cn } from "../../lib/utils";

import MainContainer from "../../components/MainContainer";
import PageTabs from "../../components/PageTabs";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import CompanyModal from "../../components/CompanyModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import useMoedaAtiva, {
	formatarMoeda,
	moedaPlataforma,
} from "../../hooks/useMoedaAtiva";

// A plataforma nem sempre cobra em real: pelo Stripe a moeda é a que o
// operador escolheu. Ver "R$" numa cobrança em euro seria mentira.
const brl = (v) => formatarMoeda(v, moedaPlataforma());
const formatarBytes = (bytes = 0) => {
        const valor = Number(bytes) || 0;
        const sinal = valor < 0 ? "-" : "";
        const absoluto = Math.abs(valor);
        if (absoluto < 1024) return `${sinal}${absoluto} B`;
        if (absoluto < 1024 * 1024) return `${sinal}${Math.round(absoluto / 1024)} KB`;
        if (absoluto < 1024 * 1024 * 1024) {
                return `${sinal}${(absoluto / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
        }
        return `${sinal}${(absoluto / 1024 / 1024 / 1024).toFixed(2).replace(".", ",")} GB`;
};

const formatarMedicao = (iso) => {
        if (!iso) return "Agora";
        try {
                return format(parseISO(iso), "dd/MM HH:mm");
        } catch (_) {
                return "Agora";
        }
};

const formatarDataHora = (iso) => {
        if (!iso) return "—";
        try {
                return format(parseISO(iso), "dd/MM/yyyy HH:mm");
        } catch (_) {
                return "—";
        }
};

const formatarDeltaStorage = (bytes, percent) => {
        if (bytes === null || bytes === undefined) return "Sem base";
        const numero = Number(bytes) || 0;
        const prefixo = numero > 0 ? "+" : "";
        const parteBytes = `${prefixo}${formatarBytes(numero)}`;
        if (percent === null || percent === undefined) return parteBytes;
        return `${parteBytes} (${prefixo}${String(percent).replace(".", ",")}%)`;
};

const resumoRecurso = {
        users: "Usuários",
        connections: "Conexões",
        queues: "Filas",
};

const RISCO = {
        baixo: {
                rotulo: "Baixo",
                classe: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
                ponto: "bg-emerald-500",
        },
        medio: {
                rotulo: "Médio",
                classe: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
                ponto: "bg-amber-500",
        },
        alto: {
                rotulo: "Alto",
                classe: "bg-destructive/10 text-destructive",
                ponto: "bg-destructive",
        },
};

const ALERTA_SEVERIDADE = {
        critical: {
                rotulo: "Crítico",
                classe: "bg-destructive/10 text-destructive ring-1 ring-destructive/15",
        },
        warning: {
                rotulo: "Atenção",
                classe: "bg-amber-500/10 text-amber-700 ring-1 ring-amber-500/15 dark:text-amber-300",
        },
        info: {
                rotulo: "Informativo",
                classe: "bg-sky-500/10 text-sky-700 ring-1 ring-sky-500/15 dark:text-sky-300",
        },
};

const TONS_CARD = {
        slate: {
                card: "from-slate-500/[0.08] via-background to-background",
                glow: "bg-slate-500/12",
                line: "from-slate-500/70 to-slate-400/30",
                icon: "bg-slate-500/10 text-slate-700 dark:text-slate-200",
                badge: "bg-slate-500/10 text-slate-700 dark:text-slate-200",
        },
        emerald: {
                card: "from-emerald-500/[0.12] via-background to-background",
                glow: "bg-emerald-500/15",
                line: "from-emerald-500 to-emerald-400/40",
                icon: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300",
                badge: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
        },
        primary: {
                card: "from-primary/12 via-background to-background",
                glow: "bg-primary/15",
                line: "from-primary to-primary/40",
                icon: "bg-primary/12 text-primary",
                badge: "bg-primary/10 text-primary",
        },
        sky: {
                card: "from-sky-500/[0.12] via-background to-background",
                glow: "bg-sky-500/15",
                line: "from-sky-500 to-sky-400/40",
                icon: "bg-sky-500/12 text-sky-700 dark:text-sky-300",
                badge: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
        },
        violet: {
                card: "from-violet-500/[0.12] via-background to-background",
                glow: "bg-violet-500/15",
                line: "from-violet-500 to-violet-400/40",
                icon: "bg-violet-500/12 text-violet-700 dark:text-violet-300",
                badge: "bg-violet-500/10 text-violet-700 dark:text-violet-300",
        },
        amber: {
                card: "from-amber-500/[0.12] via-background to-background",
                glow: "bg-amber-500/15",
                line: "from-amber-500 to-amber-400/40",
                icon: "bg-amber-500/12 text-amber-700 dark:text-amber-300",
                badge: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
        },
        rose: {
                card: "from-rose-500/[0.12] via-background to-background",
                glow: "bg-rose-500/15",
                line: "from-rose-500 to-rose-400/40",
                icon: "bg-rose-500/12 text-rose-700 dark:text-rose-300",
                badge: "bg-rose-500/10 text-rose-700 dark:text-rose-300",
        },
};

const SaasMetricCard = ({ icon: Icone, titulo, valor, subtitulo, badge, tone = "slate" }) => {
        const estilo = TONS_CARD[tone] || TONS_CARD.slate;

        return (
                <Card
                        className={cn(
                                "group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/40 bg-gradient-to-br shadow-sm transition-all",
                                "hover:-translate-y-0.5 hover:shadow-md dark:border-white/5",
                                estilo.card
                        )}
                >
                        <div
                                className={cn(
                                        "absolute inset-x-0 top-0 h-1 bg-gradient-to-r",
                                        estilo.line
                                )}
                        />
                        <div
                                className={cn(
                                        "pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full blur-2xl",
                                        estilo.glow
                                )}
                        />
                        <CardHeader className="relative pb-3">
                                <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                                                        {titulo}
                                                </p>
                                        </div>
                                        <div
                                                className={cn(
                                                        "rounded-2xl border border-white/50 p-2.5 shadow-sm dark:border-white/10",
                                                        estilo.icon
                                                )}
                                        >
                                                <Icone className="h-4 w-4" />
                                        </div>
                                </div>
                        </CardHeader>
                        <CardContent className="relative flex flex-1 flex-col pt-0">
                                <div className="min-h-[72px] text-3xl font-semibold leading-none tracking-tight text-foreground">
                                        {valor}
                                </div>
                                <p className="mt-2 min-h-[64px] text-sm text-muted-foreground">
                                        {subtitulo}
                                </p>
                                {badge && (
                                        <div className="mt-auto pt-4">
                                                <span
                                                        className={cn(
                                                                "inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium shadow-sm",
                                                                estilo.badge
                                                        )}
                                                >
                                                        {badge}
                                                </span>
                                        </div>
                                )}
                        </CardContent>
                </Card>
        );
};

// Cada situação da assinatura vira uma aba, com a cor do estado.
const ABAS = [
	{ value: "todas", rotulo: "Todas", icone: Users },
	{ value: "active", rotulo: "Ativas", icone: CircleCheck },
	{ value: "trial", rotulo: "Em teste", icone: Clock },
	{ value: "past_due", rotulo: "Inadimplentes", icone: TriangleAlert },
	{ value: "paused", rotulo: "Pausadas", icone: Pause },
	{ value: "canceled", rotulo: "Canceladas", icone: Ban },
];

const SITUACAO = {
	active: {
		rotulo: "Ativa",
		classe: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
		ponto: "bg-emerald-500",
	},
	trial: {
		rotulo: "Em teste",
		classe: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
		ponto: "bg-sky-500",
	},
	past_due: {
		rotulo: "Em atraso",
		classe: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
		ponto: "bg-amber-500",
	},
	paused: {
		rotulo: "Pausada",
		classe: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
		ponto: "bg-slate-400",
	},
	canceled: {
		rotulo: "Cancelada",
		classe: "bg-destructive/10 text-destructive",
		ponto: "bg-destructive",
	},
	blocked: {
		rotulo: "Bloqueada",
		classe: "bg-destructive/10 text-destructive",
		ponto: "bg-destructive",
	},
};

// Monograma da empresa: as iniciais num tom tirado do próprio nome, então a
// mesma empresa tem sempre a mesma cor e a lista fica reconhecível de relance
// sem precisar ler nome por nome.
const PALETA_MONOGRAMA = [
	"bg-sky-500/10 text-sky-700 dark:text-sky-300",
	"bg-violet-500/10 text-violet-700 dark:text-violet-300",
	"bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
	"bg-amber-500/10 text-amber-700 dark:text-amber-300",
	"bg-rose-500/10 text-rose-700 dark:text-rose-300",
	"bg-indigo-500/10 text-indigo-700 dark:text-indigo-300",
];

const iniciaisDoNome = (nome) =>
	String(nome || "")
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((parte) => parte[0] || "")
		.join("")
		.toUpperCase() || "?";

const paletaDoNome = (nome) => {
	const soma = String(nome || "")
		.split("")
		.reduce((total, letra) => total + letra.charCodeAt(0), 0);

	return PALETA_MONOGRAMA[soma % PALETA_MONOGRAMA.length];
};

const Companies = () => {
	// Puxa a moeda da plataforma; os formatadores acima leem dela.
	useMoedaAtiva();

	const navigate = useNavigate();
	const [companies, setCompanies] = useState([]);
	const [loading, setLoading] = useState(false);
	const [busca, setBusca] = useState("");
	const [aba, setAba] = useState("todas");
	const [contadores, setContadores] = useState({});
	const [modalOpen, setModalOpen] = useState(false);
	const [selecionada, setSelecionada] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
        const [healthOpen, setHealthOpen] = useState(false);
        const [healthLoading, setHealthLoading] = useState(false);
        const [healthCompany, setHealthCompany] = useState(null);
        const [healthData, setHealthData] = useState(null);
        const [alertsLoading, setAlertsLoading] = useState(false);
        const [alertsData, setAlertsData] = useState({ summary: {}, alerts: [] });
        const [riskFilter, setRiskFilter] = useState("todas");
        const [alertFilter, setAlertFilter] = useState("todas");
        const [onlineOnly, setOnlineOnly] = useState(false);
        const [featureSalvando, setFeatureSalvando] = useState(null);
        const [cleanupEscopo, setCleanupEscopo] = useState("grupos");
        const [cleanupOrigem, setCleanupOrigem] = useState("recebidas");
        const [cleanupIdade, setCleanupIdade] = useState("0");
        const [cleanupPreview, setCleanupPreview] = useState(null);
        const [cleanupLoading, setCleanupLoading] = useState(false);
        const [cleanupExecutando, setCleanupExecutando] = useState(false);
        const [cleanupConfirmOpen, setCleanupConfirmOpen] = useState(false);

	const carregar = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await api.get("/saas/companies", {
                                params: {
                                        searchParam: busca,
                                        status: aba,
                                        riskLevel: riskFilter,
                                        alertType: alertFilter,
                                        onlineOnly,
                                },
			});
			setCompanies(data.companies);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
        }, [busca, aba, riskFilter, alertFilter, onlineOnly]);

        const carregarAlertas = useCallback(async () => {
                setAlertsLoading(true);
                try {
                        const { data } = await api.get("/saas/alerts", {
                                params: { limit: 8 },
                        });
                        setAlertsData(data);
                } catch (err) {
                        toastError(err);
                }
                setAlertsLoading(false);
        }, []);

	// Os contadores das abas vêm dos indicadores, não da lista filtrada.
	const carregarContadores = useCallback(async () => {
		try {
			const { data } = await api.get("/saas/metrics");
			setContadores(data.empresas || {});
		} catch (err) {
			// Sem contador a tela continua utilizável.
		}
	}, []);

	useEffect(() => {
		const debounce = setTimeout(carregar, 400);
		return () => clearTimeout(debounce);
	}, [carregar]);

	useEffect(() => {
		carregarContadores();
	}, [carregarContadores]);

        useEffect(() => {
                carregarAlertas();
        }, [carregarAlertas]);

	const fecharModal = () => {
		setModalOpen(false);
		setSelecionada(null);
		carregar();
		carregarContadores();
	};

	const excluir = async (id) => {
		try {
			await api.delete(`/companies/${id}`);
			toast.success("Empresa excluída.");
			carregar();
			carregarContadores();
		} catch (err) {
			toastError(err);
		}
		setSelecionada(null);
	};

        // Botão rápido de exportar/importar fluxo. O switch muda na hora e
        // volta se o servidor recusar — recarregar a lista inteira a cada
        // clique custaria uma medição de armazenamento por empresa.
        const alternarExportImport = async (company) => {
                const ativo = !company.flowExportImport;
                setFeatureSalvando(company.id);
                setCompanies((atual) =>
                        atual.map((item) =>
                                item.id === company.id
                                        ? { ...item, flowExportImport: ativo }
                                        : item
                        )
                );
                try {
                        await api.put(
                                `/companies/${company.id}/modules/flowsExportImport/enabled`,
                                { enabled: ativo }
                        );
                        toast.success(
                                ativo
                                        ? `Exportar/importar liberado para ${company.name}.`
                                        : `Exportar/importar desativado para ${company.name}.`
                        );
                } catch (err) {
                        setCompanies((atual) =>
                                atual.map((item) =>
                                        item.id === company.id
                                                ? { ...item, flowExportImport: !ativo }
                                                : item
                                )
                        );
                        toastError(err);
                }
                setFeatureSalvando(null);
        };

        const abrirSaude = async (company) => {
                setHealthCompany(company);
                setHealthData(null);
                setCleanupEscopo("grupos");
                setCleanupOrigem("recebidas");
                setCleanupIdade("0");
                setCleanupPreview(null);
                setHealthOpen(true);
                setHealthLoading(true);
                try {
                        const { data } = await api.get(`/saas/companies/${company.id}/health`);
                        setHealthData(data);
                } catch (err) {
                        toastError(err);
                        setHealthOpen(false);
                }
                setHealthLoading(false);
        };

        const filtrosLimpeza = () => ({
                escopo: cleanupEscopo,
                origem: cleanupOrigem,
                idadeMinimaDias: Number(cleanupIdade) || 0,
        });

        const previsualizarLimpeza = async () => {
                if (!healthCompany?.id) return;
                setCleanupLoading(true);
                try {
                        const { data } = await api.post(
                                `/saas/companies/${healthCompany.id}/disk-cleanup/preview`,
                                filtrosLimpeza()
                        );
                        setCleanupPreview(data);
                } catch (err) {
                        toastError(err);
                }
                setCleanupLoading(false);
        };

        const executarLimpeza = async () => {
                if (!healthCompany?.id) return;
                setCleanupExecutando(true);
                try {
                        const { data } = await api.post(
                                `/saas/companies/${healthCompany.id}/disk-cleanup`,
                                filtrosLimpeza()
                        );
                        toast.success(
                                `Limpeza concluída: ${data.removidos} arquivo(s) e ${formatarBytes(
                                        data.bytesLiberados
                                )} liberados.`
                        );
                        setCleanupPreview(null);
                        const { data: saude } = await api.get(
                                `/saas/companies/${healthCompany.id}/health`
                        );
                        setHealthData(saude);
                        carregar();
                } catch (err) {
                        toastError(err);
                }
                setCleanupExecutando(false);
        };

	const abas = ABAS.map((item) => ({
		...item,
		contador:
			item.value === "todas"
				? contadores.total || 0
				: contadores[item.value] || 0,
	}));

        const cardsResumoAlertas = [
                {
                        titulo: "Alertas totais",
                        valor: alertsData.summary?.total || 0,
                        descricao: "ocorrências abertas na operação",
                        icone: TriangleAlert,
                        destaque: "text-foreground",
                        tone: "slate",
                        badge: "Visão geral",
                },
                {
                        titulo: "Críticos",
                        valor: alertsData.summary?.critical || 0,
                        descricao: "pedem ação imediata",
                        icone: ShieldAlert,
                        destaque: "text-destructive",
                        tone: "rose",
                        badge: "Prioridade máxima",
                },
                {
                        titulo: "Atenção",
                        valor: alertsData.summary?.warning || 0,
                        descricao: "exigem acompanhamento",
                        icone: Activity,
                        destaque: "text-amber-600 dark:text-amber-400",
                        tone: "amber",
                        badge: "Monitorar",
                },
                {
                        titulo: "Informativos",
                        valor: alertsData.summary?.info || 0,
                        descricao: "sinais úteis da operação",
                        icone: CircleCheck,
                        destaque: "text-sky-600 dark:text-sky-400",
                        tone: "sky",
                        badge: "Baixo impacto",
                },
        ];

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionada && `Excluir ${selecionada.name}?`}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionada(null);
				}}
				onConfirm={() => excluir(selecionada.id)}
			>
				Todos os dados desta empresa — usuários, conversas e conexões — serão
				removidos. Esta ação não pode ser desfeita.
			</ConfirmationModal>

			<ConfirmationModal
				title={
					healthCompany
						? `Limpar mídias de ${healthCompany.name}?`
						: "Confirmar limpeza de mídias"
				}
				open={cleanupConfirmOpen}
				onClose={() => setCleanupConfirmOpen(false)}
				onConfirm={executarLimpeza}
				confirmLabel={cleanupExecutando ? "Limpando..." : "Limpar mídias"}
			>
				{cleanupPreview
					? `Esta ação removerá ${cleanupPreview.elegiveis.files} arquivo(s) de mensagens de tickets e pode liberar ${formatarBytes(
							cleanupPreview.elegiveis.bytes
					  )}. Biblioteca de figurinhas, mídia, campanhas, chat interno, anexos, fotos de perfil e white-label não serão tocados.`
					: "Esta ação remove mídias elegíveis de tickets e não pode ser desfeita."}
			</ConfirmationModal>

			<CompanyModal
				open={modalOpen}
				onClose={fecharModal}
				companyId={selecionada?.id}
			/>

                        <Dialog
                                open={healthOpen}
                                onOpenChange={(open) => {
                                        setHealthOpen(open);
                                        if (!open) {
                                                setHealthCompany(null);
                                                setHealthData(null);
                                        }
                                }}
                        >
                        <DialogContent className="max-h-[90vh] overflow-y-auto border-0 bg-background p-0 shadow-2xl sm:max-w-6xl">
                                        <DialogHeader className="border-b bg-gradient-to-r from-primary/10 via-background to-emerald-500/10 px-6 py-5">
                                                <DialogTitle>
                                                        Saúde SaaS {healthData?.company?.name || healthCompany?.name || ""}
                                                </DialogTitle>
                                                <DialogDescription>
                                                        Visão auditável de uso, risco, acessos recentes e crescimento de armazenamento.
                                                </DialogDescription>
                                        </DialogHeader>

                                        {healthLoading ? (
                                                <div className="py-10 text-center text-sm text-muted-foreground">
                                                        Carregando dados da empresa...
                                                </div>
                                        ) : healthData ? (
                                                        <div className="space-y-5 p-6">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                                <Badge
                                                                        className={cn(
                                                                                        "border-0 shadow-sm",
                                                                                (SITUACAO[healthData.company.status] || SITUACAO.active)
                                                                                        .classe
                                                                        )}
                                                                >
                                                                        {(SITUACAO[healthData.company.status] || SITUACAO.active)
                                                                                .rotulo}
                                                                </Badge>
                                                                <Badge
                                                                        className={cn(
                                                                                        "border-0 shadow-sm",
                                                                                (RISCO[healthData.risco.level] || RISCO.baixo).classe
                                                                        )}
                                                                >
                                                                        Risco {(RISCO[healthData.risco.level] || RISCO.baixo).rotulo}
                                                                </Badge>
                                                                <span className="text-sm text-muted-foreground">
                                                                        {healthData.risco.summary}
                                                                </span>
                                                        </div>

                                                        {healthData.alerts?.length > 0 && (
                                                                <div className="rounded-2xl border bg-gradient-to-br from-amber-500/5 via-background to-background p-4 shadow-sm">
                                                                        <div className="mb-3 flex items-center gap-2">
                                                                                <TriangleAlert className="h-4 w-4 text-amber-500" />
                                                                                <p className="text-sm font-medium">
                                                                                        Alertas automáticos
                                                                                </p>
                                                                        </div>
                                                                        <div className="space-y-2">
                                                                                {healthData.alerts.map((alert) => (
                                                                                        <div
                                                                                                key={`${alert.type}-${alert.title}`}
                                                                                                className="rounded-xl border bg-background p-3 text-sm shadow-sm"
                                                                                        >
                                                                                                <div className="flex items-center justify-between gap-3">
                                                                                                        <span className="font-medium">
                                                                                                                {alert.title}
                                                                                                        </span>
                                                                                                        <span
                                                                                                                className={cn(
                                                                                                                        "rounded-full px-2.5 py-1 text-[11px] font-medium shadow-sm",
                                                                                                                        (ALERTA_SEVERIDADE[alert.severity] ||
                                                                                                                                ALERTA_SEVERIDADE.info).classe
                                                                                                                )}
                                                                                                        >
                                                                                                                {(ALERTA_SEVERIDADE[alert.severity] ||
                                                                                                                        ALERTA_SEVERIDADE.info).rotulo}
                                                                                                        </span>
                                                                                                </div>
                                                                                                <p className="mt-1 text-xs text-muted-foreground">
                                                                                                        {alert.description}
                                                                                                </p>
                                                                                        </div>
                                                                                ))}
                                                                        </div>
                                                                </div>
                                                        )}

                                                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
                                                                <SaasMetricCard
                                                                        icon={Wifi}
                                                                        titulo="Online agora"
                                                                        valor={healthData.company.onlineUsers || 0}
                                                                        subtitulo="usuários ativos neste momento"
                                                                        badge={healthData.company.onlineUsers ? "Ao vivo" : "Sem atividade agora"}
                                                                        tone="emerald"
                                                                />

                                                                <SaasMetricCard
                                                                        icon={MessageSquare}
                                                                        titulo="Tickets atendidos"
                                                                        valor={healthData.tickets?.atendidos || 0}
                                                                        subtitulo="total de atendimentos reais"
                                                                        badge="Volume operacional"
                                                                        tone="primary"
                                                                />

                                                                <SaasMetricCard
                                                                        icon={CheckCheck}
                                                                        titulo="Tickets encerrados"
                                                                        valor={healthData.tickets?.encerrados || 0}
                                                                        subtitulo="atendimentos finalizados"
                                                                        badge="Ciclo concluído"
                                                                        tone="sky"
                                                                />

                                                                <SaasMetricCard
                                                                        icon={HardDrive}
                                                                        titulo="Armazenamento atual"
                                                                        valor={formatarBytes(healthData.storage.current.bytes)}
                                                                        subtitulo={`${healthData.storage.current.files} arquivo(s) medidos`}
                                                                        badge="Uso consolidado"
                                                                        tone="violet"
                                                                />

                                                                <SaasMetricCard
                                                                        icon={TrendingUp}
                                                                        titulo="Crescimento 30 dias"
                                                                        valor={formatarDeltaStorage(
                                                                                healthData.storage.growth.bytes30d,
                                                                                healthData.storage.growth.percent30d
                                                                        )}
                                                                        subtitulo={`7 dias: ${formatarDeltaStorage(
                                                                                healthData.storage.growth.bytes7d,
                                                                                healthData.storage.growth.percent7d
                                                                        )}`}
                                                                        badge="Tendência recente"
                                                                        tone="amber"
                                                                />

                                                                <SaasMetricCard
                                                                        icon={ShieldAlert}
                                                                        titulo="Risco operacional"
                                                                        valor={(RISCO[healthData.risco.level] || RISCO.baixo).rotulo}
                                                                        subtitulo={`score ${healthData.risco.score}`}
                                                                        badge={healthData.risco.summary || "Operação estável"}
                                                                        tone="rose"
                                                                />
                                                        </div>

                                                        <div className="grid gap-4 xl:grid-cols-2">
                                                                <Card className="rounded-2xl shadow-sm">
                                                                        <CardHeader>
                                                                                <CardTitle className="text-base">
                                                                                        Armazenamento por origem
                                                                                </CardTitle>
                                                                        </CardHeader>
                                                                        <CardContent className="space-y-3">
                                                                                        <div className="rounded-xl border bg-muted/30 p-3 text-sm">
                                                                                        <p>
                                                                                                Medido em{" "}
                                                                                                {formatarMedicao(
                                                                                                        healthData.storage.current.measuredAt
                                                                                                )}
                                                                                        </p>
                                                                                        <p className="text-muted-foreground">
                                                                                                Arquivos não encontrados:{" "}
                                                                                                {healthData.storage.current.missingFiles}
                                                                                        </p>
                                                                                </div>

                                                                                <div className="space-y-2">
                                                                                        {healthData.storage.current.breakdown?.length ? (
                                                                                                healthData.storage.current.breakdown.map((item) => (
                                                                                                        <div
                                                                                                                key={item.key}
                                                                                                                className="rounded-xl border p-3 shadow-sm"
                                                                                                        >
                                                                                                                <div className="flex items-center justify-between gap-3 text-sm">
                                                                                                                        <span className="font-medium">
                                                                                                                                {item.label}
                                                                                                                        </span>
                                                                                                                        <span className="tabular-nums">
                                                                                                                                {formatarBytes(item.bytes)}
                                                                                                                        </span>
                                                                                                                </div>
                                                                                                                <p className="mt-1 text-xs text-muted-foreground">
                                                                                                                        {item.files} arquivo(s)
                                                                                                                        {item.missingFiles
                                                                                                                                ? ` · ${item.missingFiles} ausente(s)`
                                                                                                                                : ""}
                                                                                                                </p>
                                                                                                        </div>
                                                                                                ))
                                                                                        ) : (
                                                                                                <p className="text-sm text-muted-foreground">
                                                                                                        Nenhum arquivo medido.
                                                                                                </p>
                                                                                        )}
                                                                                </div>

                                                                                <div className="rounded-xl border p-3">
                                                                                        <p className="mb-2 text-sm font-medium">
                                                                                                Histórico recente
                                                                                        </p>
                                                                                        <div className="space-y-2">
                                                                                                {healthData.storage.history?.length ? (
                                                                                                        healthData.storage.history.map((item) => (
                                                                                                                <div
                                                                                                                        key={item.createdAt}
                                                                                                                        className="flex items-center justify-between gap-3 text-xs"
                                                                                                                >
                                                                                                                        <span className="text-muted-foreground">
                                                                                                                                {formatarDataHora(item.createdAt)}
                                                                                                                        </span>
                                                                                                                        <span className="tabular-nums">
                                                                                                                                {formatarBytes(item.bytes)}
                                                                                                                        </span>
                                                                                                                </div>
                                                                                                        ))
                                                                                                ) : (
                                                                                                        <p className="text-xs text-muted-foreground">
                                                                                                                O histórico começa a aparecer conforme os snapshots do SaaS são coletados.
                                                                                                        </p>
                                                                                                )}
                                                                                        </div>
                                                                                </div>
                                                                        </CardContent>
                                                                </Card>

                                                                <Card className="rounded-2xl shadow-sm">
                                                                        <CardHeader>
                                                                                <CardTitle className="text-base">
                                                                                        Consumo e limites
                                                                                </CardTitle>
                                                                        </CardHeader>
                                                                        <CardContent className="space-y-4">
                                                                                {healthData.consumo.map((item) => (
                                                                                        <div key={item.resource} className="space-y-2">
                                                                                                <div className="flex items-center justify-between gap-3 text-sm">
                                                                                                        <span className="font-medium">
                                                                                                                {resumoRecurso[item.resource] || item.resource}
                                                                                                        </span>
                                                                                                        <span className="tabular-nums text-muted-foreground">
                                                                                                                {item.used} / {item.limit || "Ilimitado"}
                                                                                                        </span>
                                                                                                </div>
                                                                                                <Progress
                                                                                                        value={item.limit > 0 ? item.percent : 0}
                                                                                                        status={
                                                                                                                item.exceeded
                                                                                                                        ? "red"
                                                                                                                        : item.nearLimit
                                                                                                                        ? "amber"
                                                                                                                        : "emerald"
                                                                                                        }
                                                                                                />
                                                                                        </div>
                                                                                ))}

                                                                                <div className="rounded-xl border bg-muted/30 p-3 text-sm">
                                                                                        {healthData.sugestaoDeUpgrade ? (
                                                                                                <span>{healthData.sugestaoDeUpgrade.motivo}</span>
                                                                                        ) : (
                                                                                                <span className="text-muted-foreground">
                                                                                                        Sem pressão de upgrade agora.
                                                                                                </span>
                                                                                        )}
                                                                                </div>

                                                                                <div className="rounded-xl border p-3 text-sm">
                                                                                        <p className="font-medium">Risco</p>
                                                                                        <ul className="mt-2 space-y-1 text-muted-foreground">
                                                                                                {healthData.risco.reasons?.length ? (
                                                                                                        healthData.risco.reasons.map((item) => (
                                                                                                                <li key={item}>• {item}</li>
                                                                                                        ))
                                                                                                ) : (
                                                                                                        <li>• Operação estável</li>
                                                                                                )}
                                                                                        </ul>
                                                                                </div>
                                                                        </CardContent>
                                                                </Card>

																<Card className="rounded-2xl shadow-sm">
																		<CardHeader>
																				<CardTitle className="text-base">
																						Limpeza de disco
																				</CardTitle>
																		</CardHeader>
																		<CardContent className="space-y-4">
																				<div className="rounded-xl border bg-muted/30 p-3 text-sm text-muted-foreground">
																						Remove apenas mídias de mensagens de tickets. Figurinhas,
																						biblioteca de mídia e demais origens ficam preservadas.
																				</div>

																				<div className="grid gap-3 md:grid-cols-3">
																						<div className="space-y-2">
																								<p className="text-sm font-medium">Escopo</p>
																								<Select
																										value={cleanupEscopo}
																										onValueChange={setCleanupEscopo}
																								>
																										<SelectTrigger className="rounded-xl">
																												<SelectValue />
																										</SelectTrigger>
																										<SelectContent>
																												<SelectItem value="grupos">
																														Somente grupos
																												</SelectItem>
																												<SelectItem value="todos">
																														Grupos e tickets
																												</SelectItem>
																										</SelectContent>
																								</Select>
																						</div>

																						<div className="space-y-2">
																								<p className="text-sm font-medium">Origem</p>
																								<Select
																										value={cleanupOrigem}
																										onValueChange={setCleanupOrigem}
																								>
																										<SelectTrigger className="rounded-xl">
																												<SelectValue />
																										</SelectTrigger>
																										<SelectContent>
																												<SelectItem value="recebidas">
																														Só recebidas
																												</SelectItem>
																												<SelectItem value="enviadas">
																														Só enviadas
																												</SelectItem>
																												<SelectItem value="todas">
																														Todas
																												</SelectItem>
																										</SelectContent>
																								</Select>
																						</div>

																						<div className="space-y-2">
																								<p className="text-sm font-medium">
																										Idade mínima (dias)
																								</p>
																								<Input
																										type="number"
																										min="0"
																										step="1"
																										value={cleanupIdade}
																										onChange={(e) => setCleanupIdade(e.target.value)}
																										className="rounded-xl"
																								/>
																						</div>
																				</div>

																				<div className="flex flex-wrap gap-2">
																						<Button
																								variant="outline"
																								className="rounded-xl"
																								onClick={previsualizarLimpeza}
																								disabled={cleanupLoading}
																						>
																								{cleanupLoading ? "Calculando..." : "Pré-visualizar"}
																						</Button>
																						<Button
																								className="rounded-xl"
																								variant="destructive"
																								onClick={() => setCleanupConfirmOpen(true)}
																								disabled={
																										cleanupExecutando ||
																										!cleanupPreview ||
																										!cleanupPreview.elegiveis?.files
																								}
																						>
																								{cleanupExecutando ? "Limpando..." : "Executar limpeza"}
																						</Button>
																				</div>

																				{cleanupPreview ? (
																						<div className="space-y-3 rounded-2xl border p-4">
																								<div className="grid gap-3 sm:grid-cols-3">
																										<div className="rounded-xl border bg-muted/20 p-3">
																												<p className="text-xs uppercase tracking-wide text-muted-foreground">
																														Arquivos elegíveis
																												</p>
																												<p className="mt-1 text-lg font-semibold">
																														{cleanupPreview.elegiveis.files}
																												</p>
																										</div>
																										<div className="rounded-xl border bg-muted/20 p-3">
																												<p className="text-xs uppercase tracking-wide text-muted-foreground">
																														Espaço estimado
																												</p>
																												<p className="mt-1 text-lg font-semibold">
																														{formatarBytes(cleanupPreview.elegiveis.bytes)}
																												</p>
																										</div>
																										<div className="rounded-xl border bg-muted/20 p-3">
																												<p className="text-xs uppercase tracking-wide text-muted-foreground">
																														Arquivos ausentes
																												</p>
																												<p className="mt-1 text-lg font-semibold">
																														{cleanupPreview.elegiveis.ausentes}
																												</p>
																										</div>
																								</div>

																								<div className="rounded-xl border bg-muted/20 p-3 text-sm">
																										<p>
																												Escopo:{" "}
																												<span className="font-medium">
																														{cleanupPreview.filtros.escopo === "grupos"
																																? "Somente grupos"
																																: "Grupos e tickets"}
																												</span>
																										</p>
																										<p className="mt-1">
																												Origem:{" "}
																												<span className="font-medium capitalize">
																														{cleanupPreview.filtros.origem}
																												</span>
																										</p>
																										<p className="mt-1">
																												Idade mínima:{" "}
																												<span className="font-medium">
																														{cleanupPreview.filtros.idadeMinimaDias || 0} dia(s)
																												</span>
																										</p>
																								</div>

																								{cleanupPreview.porTipo?.length ? (
																										<div className="space-y-2">
																												<p className="text-sm font-medium">
																														Distribuição por tipo
																												</p>
																												{cleanupPreview.porTipo.map((item) => (
																														<div
																																key={item.tipo}
																																className="flex items-center justify-between gap-3 rounded-xl border p-3 text-sm"
																														>
																																<span className="font-medium capitalize">
																																		{item.tipo}
																																</span>
																																<span className="text-muted-foreground">
																																		{item.files} referência(s)
																																</span>
																														</div>
																												))}
																										</div>
																								) : null}
																						</div>
																				) : (
																						<p className="text-sm text-muted-foreground">
																								Use a pré-visualização para ver quantos arquivos de
																								tickets podem ser removidos antes de confirmar.
																						</p>
																				)}
																		</CardContent>
																</Card>

                                                                <Card className="rounded-2xl shadow-sm">
                                                                        <CardHeader>
                                                                                <CardTitle className="text-base">
                                                                                        Usuários online agora
                                                                                </CardTitle>
                                                                        </CardHeader>
                                                                        <CardContent className="space-y-2">
                                                                                {healthData.usuariosOnline?.length ? (
                                                                                        healthData.usuariosOnline.map((item) => (
                                                                                                <div
                                                                                                        key={item.id}
                                                                                                        className="flex items-center justify-between gap-3 rounded-xl border p-3 text-sm shadow-sm"
                                                                                                >
                                                                                                        <span className="font-medium">{item.name}</span>
                                                                                                        <span className="text-muted-foreground capitalize">
                                                                                                                {item.presenceStatus}
                                                                                                        </span>
                                                                                                </div>
                                                                                        ))
                                                                                ) : (
                                                                                        <p className="text-sm text-muted-foreground">
                                                                                                Nenhum usuário online no momento.
                                                                                        </p>
                                                                                )}
                                                                        </CardContent>
                                                                </Card>

                                                                <Card className="rounded-2xl shadow-sm">
                                                                        <CardHeader>
                                                                                <CardTitle className="text-base">
                                                                                        Sessões recentes
                                                                                </CardTitle>
                                                                        </CardHeader>
                                                                        <CardContent className="space-y-2">
                                                                                {healthData.sessoesRecentes?.length ? (
                                                                                        healthData.sessoesRecentes.map((item) => (
                                                                                                <div
                                                                                                        key={item.id}
                                                                                                        className="rounded-xl border p-3 text-sm shadow-sm"
                                                                                                >
                                                                                                        <div className="flex items-center justify-between gap-3">
                                                                                                                <span className="font-medium">
                                                                                                                        {item.user?.name || "Usuário"}
                                                                                                                </span>
                                                                                                                <span
                                                                                                                        className={cn(
                                                                                                                                "text-xs",
                                                                                                                                item.isActive
                                                                                                                                        ? "text-emerald-600 dark:text-emerald-400"
                                                                                                                                        : "text-muted-foreground"
                                                                                                                        )}
                                                                                                                >
                                                                                                                        {item.isActive ? "Ativa" : "Encerrada"}
                                                                                                                </span>
                                                                                                        </div>
                                                                                                        <p className="mt-1 text-xs text-muted-foreground">
                                                                                                                Login: {formatarDataHora(item.loggedInAt)}
                                                                                                        </p>
                                                                                                        <p className="text-xs text-muted-foreground">
                                                                                                                IP: {item.ip || "—"}
                                                                                                        </p>
                                                                                                        {item.device && (
                                                                                                                <p className="text-xs text-muted-foreground">
                                                                                                                        Dispositivo: {item.device}
                                                                                                                </p>
                                                                                                        )}
                                                                                                </div>
                                                                                        ))
                                                                                ) : (
                                                                                        <p className="text-sm text-muted-foreground">
                                                                                                Sem sessões recentes registradas.
                                                                                        </p>
                                                                                )}
                                                                        </CardContent>
                                                                </Card>
                                                        </div>

                                                        <Separator />

                                                        <Card className="rounded-2xl shadow-sm">
                                                                <CardHeader>
                                                                        <CardTitle className="text-base">
                                                                                Timeline auditável
                                                                        </CardTitle>
                                                                </CardHeader>
                                                                <CardContent className="space-y-2">
                                                                        {healthData.timeline?.length ? (
                                                                                healthData.timeline.map((item) => (
                                                                                        <div
                                                                                                key={item.id}
                                                                                                className="rounded-xl border p-3 shadow-sm"
                                                                                        >
                                                                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                                                                        <div className="flex items-center gap-2">
                                                                                                                <span
                                                                                                                        className={cn(
                                                                                                                                "inline-block rounded-full px-2.5 py-1 text-[11px] font-medium shadow-sm",
                                                                                                                                (ALERTA_SEVERIDADE[item.severity] ||
                                                                                                                                        ALERTA_SEVERIDADE.info).classe
                                                                                                                        )}
                                                                                                                >
                                                                                                                        {item.type}
                                                                                                                </span>
                                                                                                                <span className="text-sm font-medium">
                                                                                                                        {item.title}
                                                                                                                </span>
                                                                                                        </div>
                                                                                                        <span className="text-xs text-muted-foreground">
                                                                                                                {formatarDataHora(item.at)}
                                                                                                        </span>
                                                                                                </div>
                                                                                                <p className="mt-1 text-xs text-muted-foreground">
                                                                                                        {item.description}
                                                                                                </p>
                                                                                        </div>
                                                                                ))
                                                                        ) : (
                                                                                <p className="text-sm text-muted-foreground">
                                                                                        Sem eventos recentes para esta empresa.
                                                                                </p>
                                                                        )}
                                                                </CardContent>
                                                        </Card>
                                                </div>
                                        ) : (
                                                <div className="py-10 text-center text-sm text-muted-foreground">
                                                        Não foi possível carregar a saúde desta empresa.
                                                </div>
                                        )}
                                </DialogContent>
                        </Dialog>

                        <div className="px-4 pt-4 sm:px-6">
                                <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-background via-background to-muted/60 shadow-sm">
                                        {/* Brilho suave no canto: dá profundidade ao cabeçalho
                                            sem virar faixa colorida. */}
                                        <div className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-primary/10 blur-3xl" />
                                        <div className="h-[3px] w-full bg-gradient-to-r from-primary via-primary/70 to-primary/20" />
                                        <div className="relative flex flex-col gap-3 px-4 py-3.5 sm:px-5">
                                                <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                                                        <div className="flex min-w-0 items-center gap-3">
                                                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                                                        <Building2 className="h-4 w-4" />
                                                                </span>
                                                                <div className="min-w-0">
                                                                        <div className="flex items-center gap-2">
                                                                                <h1 className="text-lg font-semibold tracking-tight text-foreground">
                                                                                        Empresas
                                                                                </h1>
                                                                                <Badge className="border-0 bg-primary/10 px-2 py-0 text-[10px] font-medium uppercase tracking-wide text-primary">
                                                                                        Gestão SaaS
                                                                                </Badge>
                                                                        </div>
                                                                        <p className="truncate text-xs text-muted-foreground">
                                                                                Alertas automáticos, risco, armazenamento e saúde de cada cliente.
                                                                        </p>
                                                                </div>
                                                        </div>

                                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                                                <div className="relative">
                                                                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                                        <Input
                                                                                placeholder="Nome, e-mail, documento…"
                                                                                value={busca}
                                                                                onChange={(e) => setBusca(e.target.value)}
                                                                                className="h-9 w-full rounded-xl border-background/60 bg-background/90 pl-9 text-sm shadow-sm sm:w-72"
                                                                        />
                                                                </div>
                                                                <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="h-9 rounded-xl px-3 shadow-sm"
                                                                        onClick={() => navigate("/global-config")}
                                                                >
                                                                        <Wrench className="h-4 w-4" />
                                                                        Modo manutenção
                                                                </Button>
                                                                <Button
                                                                        size="sm"
                                                                        className="h-9 rounded-xl px-3 shadow-sm"
                                                                        onClick={() => {
                                                                                setSelecionada(null);
                                                                                setModalOpen(true);
                                                                        }}
                                                                >
                                                                        <Plus className="h-4 w-4" />
                                                                        Nova empresa
                                                                </Button>
                                                        </div>
                                                </div>

                                                {/* Filtros e números na mesma faixa: antes eram dois blocos
                                                    empilhados dizendo o que os rótulos já dizem. */}
                                                <div className="flex flex-col gap-3 border-t pt-3 xl:flex-row xl:items-center xl:justify-between">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                                <span className="flex items-center gap-1.5 pr-1 text-xs font-medium text-muted-foreground">
                                                                        <Filter className="h-3.5 w-3.5 text-primary" />
                                                                        Filtros
                                                                </span>

                                                                <div className="w-full sm:w-40">
                                                                        <Select value={riskFilter} onValueChange={setRiskFilter}>
                                                                                <SelectTrigger className="h-9 rounded-xl bg-background text-sm">
                                                                                        <SelectValue placeholder="Risco" />
                                                                                </SelectTrigger>
                                                                                <SelectContent>
                                                                                        <SelectItem value="todas">Todo risco</SelectItem>
                                                                                        <SelectItem value="alto">Risco alto</SelectItem>
                                                                                        <SelectItem value="medio">Risco médio</SelectItem>
                                                                                        <SelectItem value="baixo">Risco baixo</SelectItem>
                                                                                </SelectContent>
                                                                        </Select>
                                                                </div>

                                                                <div className="w-full sm:w-52">
                                                                        <Select value={alertFilter} onValueChange={setAlertFilter}>
                                                                                <SelectTrigger className="h-9 rounded-xl bg-background text-sm">
                                                                                        <SelectValue placeholder="Alertas" />
                                                                                </SelectTrigger>
                                                                                <SelectContent>
                                                                                        <SelectItem value="todas">Todos os alertas</SelectItem>
                                                                                        <SelectItem value="risk_high">Risco alto</SelectItem>
                                                                                        <SelectItem value="overdue">Em atraso</SelectItem>
                                                                                        <SelectItem value="inactive">Sem uso recente</SelectItem>
                                                                                        <SelectItem value="near_limit">Perto do limite</SelectItem>
                                                                                        <SelectItem value="storage_growth">Crescimento de storage</SelectItem>
                                                                                </SelectContent>
                                                                        </Select>
                                                                </div>

                                                                <label className="flex h-9 items-center gap-2 rounded-xl border bg-background px-3 text-xs shadow-sm">
                                                                        <Switch
                                                                                checked={onlineOnly}
                                                                                onCheckedChange={setOnlineOnly}
                                                                        />
                                                                        Só online agora
                                                                </label>

                                                                {(riskFilter !== "todas" ||
                                                                        alertFilter !== "todas" ||
                                                                        onlineOnly) && (
                                                                        <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                className="h-9 rounded-xl text-xs"
                                                                                onClick={() => {
                                                                                        setRiskFilter("todas");
                                                                                        setAlertFilter("todas");
                                                                                        setOnlineOnly(false);
                                                                                }}
                                                                        >
                                                                                Limpar filtros
                                                                        </Button>
                                                                )}
                                                        </div>

                                                        {/* Números em fita única, separados por fio: ocupam
                                                            uma linha em vez de três cartões. */}
                                                        <div className="flex items-stretch divide-x divide-border self-start overflow-hidden rounded-xl border bg-background/80 shadow-sm xl:self-auto">
                                                                <div className="px-3 py-1.5">
                                                                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                                                                Empresas
                                                                        </p>
                                                                        <p className="text-lg font-semibold leading-tight tabular-nums">
                                                                                {contadores.total || 0}
                                                                        </p>
                                                                </div>
                                                                <div className="px-3 py-1.5">
                                                                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                                                                Ativas
                                                                        </p>
                                                                        <p className="text-lg font-semibold leading-tight tabular-nums text-emerald-600 dark:text-emerald-400">
                                                                                {contadores.active || 0}
                                                                        </p>
                                                                </div>
                                                                <div className="px-3 py-1.5">
                                                                        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                                                                Com alertas
                                                                        </p>
                                                                        <p className="text-lg font-semibold leading-tight tabular-nums text-amber-600 dark:text-amber-400">
                                                                                {alertsData.summary?.total || 0}
                                                                        </p>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                        </div>

                        <div className="px-4 pt-3 sm:px-6">
                                <PageTabs abas={abas} atual={aba} onChange={setAba} />
                        </div>

                        <div className="flex-1 overflow-auto p-4 sm:p-5">
                                <div className="mb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                        {cardsResumoAlertas.map((card) => {
                                                const Icone = card.icone;
                                                const estilo = TONS_CARD[card.tone] || TONS_CARD.slate;
                                                return (
                                                        <Card
                                                                key={card.titulo}
                                                                className={cn(
                                                                        "group relative overflow-hidden rounded-2xl border border-white/40 bg-gradient-to-br shadow-sm transition-all",
                                                                        "hover:-translate-y-0.5 hover:shadow-md dark:border-white/5",
                                                                        estilo.card
                                                                )}
                                                        >
                                                                <div
                                                                        className={cn(
                                                                                "absolute inset-x-0 top-0 h-1 bg-gradient-to-r",
                                                                                estilo.line
                                                                        )}
                                                                />
                                                                <div
                                                                        className={cn(
                                                                                "pointer-events-none absolute -right-5 -top-5 h-20 w-20 rounded-full blur-2xl",
                                                                                estilo.glow
                                                                        )}
                                                                />
                                                                <CardContent className="relative flex items-center gap-3 p-3.5">
                                                                        <div
                                                                                className={cn(
                                                                                        "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/50 shadow-sm dark:border-white/10",
                                                                                        estilo.icon
                                                                                )}
                                                                        >
                                                                                <Icone className="h-4 w-4" />
                                                                        </div>
                                                                        <div className="min-w-0 flex-1">
                                                                                <div className="flex items-baseline justify-between gap-2">
                                                                                        <p className="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                                                                                {card.titulo}
                                                                                        </p>
                                                                                        <span
                                                                                                className={cn(
                                                                                                        "shrink-0 text-2xl font-semibold leading-none tracking-tight",
                                                                                                        card.destaque
                                                                                                )}
                                                                                        >
                                                                                                {card.valor}
                                                                                        </span>
                                                                                </div>
                                                                                <p className="mt-1 truncate text-xs text-muted-foreground">
                                                                                        {card.descricao}
                                                                                </p>
                                                                                <span
                                                                                        className={cn(
                                                                                                "mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium",
                                                                                                estilo.badge
                                                                                        )}
                                                                                >
                                                                                        {card.badge}
                                                                                </span>
                                                                        </div>
                                                                </CardContent>
                                                        </Card>
                                                );
                                        })}
                                </div>

                                <Card className="mb-3 overflow-hidden rounded-2xl border-0 bg-gradient-to-br from-background via-background to-muted/60 shadow-sm">
                                        <CardHeader className="px-4 pb-2 pt-3.5">
                                                <CardTitle className="text-sm">
                                                        Alertas automáticos da operação
                                                </CardTitle>
                                                <p className="text-xs text-muted-foreground">
                                                        Clique para abrir a visão auditável da empresa e agir mais rápido.
                                                </p>
                                        </CardHeader>
                                        <CardContent className="grid gap-2 px-4 pb-4 sm:grid-cols-2">
                                                {alertsLoading ? (
                                                        <p className="text-sm text-muted-foreground sm:col-span-2">
                                                                Carregando alertas...
                                                        </p>
                                                ) : alertsData.alerts?.length ? (
                                                        alertsData.alerts.map((alert) => (
                                                                <button
                                                                        key={`${alert.companyId}-${alert.type}-${alert.title}`}
                                                                        type="button"
                                                                        onClick={() =>
                                                                                abrirSaude({
                                                                                        id: alert.companyId,
                                                                                        name: alert.companyName,
                                                                                })
                                                                        }
                                                                        className="flex w-full items-center justify-between gap-3 rounded-xl border bg-background/90 px-3 py-2 text-left shadow-sm transition-colors hover:bg-muted/30"
                                                                >
                                                                        <div className="min-w-0">
                                                                                <p className="truncate text-sm font-medium text-foreground">
                                                                                        {alert.companyName}
                                                                                </p>
                                                                                <p className="truncate text-[11px] text-muted-foreground">
                                                                                        {alert.title} — {alert.description}
                                                                                </p>
                                                                        </div>
                                                                        <span
                                                                                className={cn(
                                                                                        "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
                                                                                        (ALERTA_SEVERIDADE[alert.severity] ||
                                                                                                ALERTA_SEVERIDADE.info).classe
                                                                                )}
                                                                        >
                                                                                {(ALERTA_SEVERIDADE[alert.severity] ||
                                                                                        ALERTA_SEVERIDADE.info).rotulo}
                                                                        </span>
                                                                </button>
                                                        ))
                                                ) : (
                                                        <p className="text-sm text-muted-foreground sm:col-span-2">
                                                                Nenhum alerta automático no momento.
                                                        </p>
                                                )}
                                        </CardContent>
                                </Card>

				{/* Cartões no mobile: tabela em tela estreita fica ilegível. */}
				<div className="space-y-2 sm:hidden">
					{companies.map((company) => {
						const situacao = SITUACAO[company.status] || SITUACAO.active;
                                                const risco = RISCO[company.risk?.level] || RISCO.baixo;
						return (
                                                        <div
                                                                key={company.id}
                                                                className="rounded-xl border bg-card p-3 text-left shadow-sm"
                                                        >
                                                                <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                                navigate(`/companies/${company.id}/subscription`)
                                                                        }
                                                                        className="w-full text-left"
                                                                >
                                                                        <div className="flex items-start justify-between gap-2">
                                                                                <div className="flex min-w-0 items-center gap-2">
                                                                                        <span
                                                                                                className={cn(
                                                                                                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-[11px] font-semibold",
                                                                                                        paletaDoNome(company.name)
                                                                                                )}
                                                                                        >
                                                                                                {iniciaisDoNome(company.name)}
                                                                                        </span>
                                                                                        <div className="min-w-0">
                                                                                                <p className="truncate text-sm font-medium">
                                                                                                        {company.name}
                                                                                                </p>
                                                                                                {company.email && (
                                                                                                        <p className="truncate text-[11px] text-muted-foreground">
                                                                                                                {company.email}
                                                                                                        </p>
                                                                                                )}
                                                                                        </div>
                                                                                </div>
                                                                                <span
                                                                                        className={cn(
                                                                                                "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium",
                                                                                                situacao.classe
                                                                                        )}
                                                                                >
                                                                                        <span className={cn("h-1.5 w-1.5 rounded-full", situacao.ponto)} />
                                                                                        {situacao.rotulo}
                                                                                </span>
                                                                        </div>
                                                                        <div className="mt-2 flex items-center justify-between text-xs">
                                                                                <span className="text-muted-foreground">
                                                                                        {company.plan?.name || "Sem plano"}
                                                                                </span>
                                                                                <span className="font-medium tabular-nums">
                                                                                        {brl(company.total)}
                                                                                </span>
                                                                        </div>
                                                                        <div className="mt-2 flex items-center justify-between text-xs">
                                                                                <span
                                                                                        className={cn(
                                                                                                "font-medium",
                                                                                                company.isOnline
                                                                                                        ? "text-emerald-600 dark:text-emerald-400"
                                                                                                        : "text-muted-foreground"
                                                                                        )}
                                                                                >
                                                                                        {company.isOnline
                                                                                                ? `${company.onlineUsers} online`
                                                                                                : "Offline"}
                                                                                </span>
                                                                                <span className="tabular-nums text-muted-foreground">
                                                                                        {formatarBytes(company.storage?.bytes || 0)}
                                                                                </span>
                                                                        </div>
                                                                        <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                                                                                <span
                                                                                        className={cn(
                                                                                                "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-medium",
                                                                                                risco.classe
                                                                                        )}
                                                                                >
                                                                                        <span className={cn("h-1.5 w-1.5 rounded-full", risco.ponto)} />
                                                                                        Risco {risco.rotulo}
                                                                                </span>
                                                                                <span className="truncate text-muted-foreground">
                                                                                        {company.risk?.summary || "Operação estável"}
                                                                                </span>
                                                                        </div>
                                                                </button>

                                                                <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border bg-muted/20 px-3 py-1.5">
                                                                        <span className="text-xs text-muted-foreground">
                                                                                Exportar/importar fluxo
                                                                        </span>
                                                                        <Switch
                                                                                checked={!!company.flowExportImport}
                                                                                disabled={featureSalvando === company.id}
                                                                                onCheckedChange={() => alternarExportImport(company)}
                                                                                aria-label={`Exportar e importar fluxos de ${company.name}`}
                                                                        />
                                                                </div>

                                                                <div className="mt-2">
                                                                        <Button
                                                                                variant="outline"
                                                                                size="sm"
                                                                                className="w-full"
                                                                                onClick={() => abrirSaude(company)}
                                                                        >
                                                                                <Activity className="h-4 w-4" />
                                                                                Saúde SaaS
                                                                        </Button>
                                                                </div>
                                                        </div>
						);
					})}
					{!loading && companies.length === 0 && (
						<p className="py-10 text-center text-sm text-muted-foreground">
							<span className="flex flex-col items-center gap-2">
											<span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-muted/60">
												<Building2 className="h-5 w-5" />
											</span>
											<span>Nenhuma empresa nesta situação.</span>
										</span>
						</p>
					)}
				</div>

				{/* Tabela no desktop */}
                                <div className="hidden overflow-hidden rounded-2xl border bg-card shadow-sm sm:block">
                                        <div className="flex items-center justify-between gap-3 border-b bg-gradient-to-r from-background via-background to-muted/40 px-4 py-2.5">
                                                <div className="flex min-w-0 items-baseline gap-2">
                                                        <h2 className="shrink-0 text-sm font-semibold text-foreground">
                                                                Carteira de clientes
                                                        </h2>
                                                        <span className="truncate text-xs text-muted-foreground">
                                                                presença, risco, armazenamento, plano e ações por empresa
                                                        </span>
                                                </div>
                                                <Badge className="shrink-0 border-0 bg-primary/10 text-primary">
                                                        {companies.length} resultado(s)
                                                </Badge>
                                        </div>
					<Table className="table-fixed [&_td]:overflow-hidden [&_td]:px-2 [&_th]:px-2 [&_th]:text-[10px] [&_th]:leading-tight">
                                                {/* Larguras fixas por coluna: sem isto a coluna de nome
                                                    (nowrap) empurra a tabela e cria rolagem horizontal.
                                                    A 1ª coluna fica sem largura e absorve o espaço restante. */}
                                                <colgroup>
                                                        <col />
                                                        <col className="w-[112px]" />
                                                        <col className="w-[112px]" />
                                                        <col className="w-[120px]" />
                                                        <col className="w-[96px]" />
                                                        <col className="w-[112px]" />
                                                        <col className="w-[104px]" />
                                                        <col className="w-[112px]" />
                                                        <col className="w-[96px]" />
                                                        <col className="w-[168px]" />
                                                </colgroup>
                                                <TableHeader className="sticky top-0 z-10 bg-card">
							<TableRow>
                                                                <TableHead className="h-9 text-[11px] uppercase tracking-wide text-muted-foreground">
                                                                        Empresa
                                                                </TableHead>
                                                                <TableHead className="h-9 text-center text-[11px] uppercase tracking-wide text-muted-foreground">
                                                                        Online agora
                                                                </TableHead>
                                                                <TableHead className="h-9 text-center text-[11px] uppercase tracking-wide text-muted-foreground">
                                                                        Armazenamento
                                                                </TableHead>
                                                                <TableHead className="h-9 text-center text-[11px] uppercase tracking-wide text-muted-foreground">
                                                                        Risco
                                                                </TableHead>
                                                                <TableHead className="h-9 text-[11px] uppercase tracking-wide text-muted-foreground">
                                                                        Plano
                                                                </TableHead>
                                                                <TableHead className="h-9 text-right text-[11px] uppercase tracking-wide text-muted-foreground">
                                                                        Mensalidade
                                                                </TableHead>
                                                                <TableHead className="h-9 text-center text-[11px] uppercase tracking-wide text-muted-foreground">
                                                                        Vencimento
                                                                </TableHead>
                                                                <TableHead className="h-9 text-center text-[11px] uppercase tracking-wide text-muted-foreground">
                                                                        Situação
                                                                </TableHead>
                                                                <TableHead className="h-9 text-center text-[11px] uppercase tracking-wide text-muted-foreground">
                                                                        Exportar/Importar
                                                                </TableHead>
                                                                <TableHead className="h-9 text-center text-[11px] uppercase tracking-wide text-muted-foreground">
                                                                        Ações
                                                                </TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{companies.map((company) => {
								const situacao = SITUACAO[company.status] || SITUACAO.active;
                                                                const risco = RISCO[company.risk?.level] || RISCO.baixo;
								return (
                                                                        <TableRow
                                                                                key={company.id}
                                                                                className="group transition-colors hover:bg-muted/30"
                                                                        >
                                                                                <TableCell className="relative py-3">
                                                                                        {/* Fio na borda esquerda ao passar o mouse:
                                                                                            marca a linha inteira sem precisar
                                                                                            escurecer o fundo da tabela. */}
                                                                                        <span className="pointer-events-none absolute inset-y-2 left-0 w-[3px] rounded-full bg-primary opacity-0 transition-opacity group-hover:opacity-100" />
                                                                                        <div className="flex items-center gap-2.5">
                                                                                                <span
                                                                                                        className={cn(
                                                                                                                "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-[11px] font-semibold",
                                                                                                                paletaDoNome(company.name)
                                                                                                        )}
                                                                                                >
                                                                                                        {iniciaisDoNome(company.name)}
                                                                                                </span>
                                                                                                <div className="min-w-0">
                                                                                                        <span className="block truncate text-sm font-medium">
                                                                                                                {company.name}
                                                                                                        </span>
                                                                                                        <div className="flex flex-wrap items-center gap-1">
                                                                                                                {company.email && (
                                                                                                                        <span className="truncate text-[11px] text-muted-foreground">
                                                                                                                                {company.email}
                                                                                                                        </span>
                                                                                                                )}
                                                                                                                {company.tags?.map((tag) => (
                                                                                                                        <span
                                                                                                                                key={tag.id}
                                                                                                                                className="rounded-full px-1.5 text-[10px] font-medium text-white"
                                                                                                                                style={{ backgroundColor: tag.color }}
                                                                                                                        >
                                                                                                                                {tag.name}
                                                                                                                        </span>
                                                                                                                ))}
                                                                                                        </div>
                                                                                                </div>
                                                                                        </div>
                                                                                </TableCell>
                                                                                <TableCell className="py-3 text-center">
                                                                                        <span
                                                                                                className={cn(
                                                                                                        // Largura fixa: sem isto o rótulo varia ("Offline" x "12 online")
                                                                                                        // e as pílulas ficam desalinhadas entre as linhas.
                                                                                                        "inline-flex w-[96px] max-w-full items-center justify-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums",
                                                                                                        company.isOnline
                                                                                                                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                                                                                                : "bg-slate-500/10 text-slate-600 dark:text-slate-400"
                                                                                                )}
                                                                                        >
                                                                                                {/* Ponto vivo quando tem gente
                                                                                                    dentro: o olho pega a empresa
                                                                                                    em uso antes de ler o número. */}
                                                                                                <span className="relative flex h-1.5 w-1.5 shrink-0">
                                                                                                        {company.isOnline && (
                                                                                                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />
                                                                                                        )}
                                                                                                        <span
                                                                                                                className={cn(
                                                                                                                        "relative inline-flex h-1.5 w-1.5 rounded-full",
                                                                                                                        company.isOnline
                                                                                                                                ? "bg-emerald-500"
                                                                                                                                : "bg-slate-400"
                                                                                                                )}
                                                                                                        />
                                                                                                </span>
                                                                                                {company.isOnline
                                                                                                        ? `${company.onlineUsers} online`
                                                                                                        : "Offline"}
                                                                                        </span>
                                                                                </TableCell>
                                                                                <TableCell className="py-3 text-center">
                                                                                        {/* Sem caixa em volta: o cabeçalho já
                                                                                            nomeia a coluna e o tooltip entrega
                                                                                            arquivos, faltantes e data da medição. */}
                                                                                        <div
                                                                                                className="inline-flex w-full min-w-0 flex-col items-center text-sm tabular-nums"
                                                                                                title={`Arquivos medidos: ${
                                                                                                        company.storage?.files || 0
                                                                                                } · Não encontrados: ${
                                                                                                        company.storage?.missingFiles || 0
                                                                                                } · Medido em ${formatarMedicao(
                                                                                                        company.storage?.measuredAt
                                                                                                )}`}
                                                                                        >
                                                                                                <span className="font-medium">
                                                                                                        {formatarBytes(company.storage?.bytes || 0)}
                                                                                                </span>
                                                                                                <span className="text-[11px] text-muted-foreground">
                                                                                                        {company.storage?.files || 0} arquivo(s)
                                                                                                </span>
                                                                                        </div>
                                                                                </TableCell>
                                                                                <TableCell className="py-3 text-center">
                                                                                        <div className="space-y-0.5">
                                                                                                <span
                                                                                                        className={cn(
                                                                                                                "inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium",
                                                                                                                risco.classe
                                                                                                        )}
                                                                                                >
                                                                                                        <span className={cn("h-1.5 w-1.5 rounded-full", risco.ponto)} />
                                                                                                        {risco.rotulo}
                                                                                                </span>
                                                                                                <span
                                                                                                        className="mx-auto block max-w-[150px] truncate text-[11px] text-muted-foreground"
                                                                                                        title={company.risk?.summary || "Operação estável"}
                                                                                                >
                                                                                                        {company.risk?.summary || "Operação estável"}
                                                                                                </span>
                                                                                        </div>
                                                                                </TableCell>
                                                                                <TableCell className="py-3">
                                                                                        {company.plan?.name ? (
                                                                                                <span className="inline-block max-w-full truncate rounded-md bg-muted/60 px-1.5 py-0.5 align-middle text-[11px] font-medium text-foreground/80">
                                                                                                        {company.plan.name}
                                                                                                </span>
                                                                                        ) : (
                                                                                                <span className="text-muted-foreground">—</span>
                                                                                        )}
                                                                                </TableCell>
                                                                                <TableCell className="py-3 text-right text-sm font-medium tabular-nums">
                                                                                        {brl(company.total)}
                                                                                </TableCell>
                                                                                <TableCell className="py-3 text-center tabular-nums">
											{company.dueDate
												? format(parseISO(company.dueDate), "dd/MM/yyyy")
												: "—"}
										</TableCell>
                                                                                <TableCell className="py-3 text-center">
                                                                                        <span
                                                                                                className={cn(
                                                                                                        "inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium",
                                                                                                        situacao.classe
                                                                                                )}
                                                                                        >
                                                                                                <span className={cn("h-1.5 w-1.5 rounded-full", situacao.ponto)} />
                                                                                                {situacao.rotulo}
                                                                                        </span>
                                                                                </TableCell>
                                                                                <TableCell className="py-3 text-center">
                                                                                        <div className="flex justify-center">
                                                                                                <Switch
                                                                                                        checked={!!company.flowExportImport}
                                                                                                        disabled={featureSalvando === company.id}
                                                                                                        onCheckedChange={() => alternarExportImport(company)}
                                                                                                        aria-label={`Exportar e importar fluxos de ${company.name}`}
                                                                                                />
                                                                                        </div>
                                                                                </TableCell>
                                                                                <TableCell className="py-3">
                                                                                        <div className="flex items-center justify-center gap-0.5">
												<Button
                                                                                                        variant="ghost"
                                                                                                        size="icon"
                                                                                                        className="h-7 w-7 rounded-lg"
                                                                                                        aria-label="Ver saúde SaaS"
                                                                                                        onClick={() => abrirSaude(company)}
                                                                                                >
                                                                                                        <Activity className="h-4 w-4" />
                                                                                                </Button>
                                                                                                <Button
													variant="ghost"
													size="icon"
													className="h-7 w-7 rounded-lg"
													aria-label="Gerenciar assinatura"
													onClick={() =>
														navigate(
															`/companies/${company.id}/subscription`
														)
													}
												>
													<CreditCard className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7 rounded-lg"
													aria-label="Ver faturas"
													onClick={() =>
														navigate(`/invoices?companyId=${company.id}`)
													}
												>
													<Receipt className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7 rounded-lg"
													aria-label="Editar empresa"
													onClick={() => {
														setSelecionada(company);
														setModalOpen(true);
													}}
												>
													<Pencil className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7 rounded-lg"
													aria-label="Excluir empresa"
													onClick={() => {
														setSelecionada(company);
														setConfirmOpen(true);
													}}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								);
							})}
                                                        {loading && <TableRowSkeleton columns={10} />}
							{!loading && companies.length === 0 && (
								<TableRow>
									<TableCell
                                                                                colSpan={10}
										className="py-10 text-center text-sm text-muted-foreground"
									>
										<span className="flex flex-col items-center gap-2">
											<span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-muted/60">
												<Building2 className="h-5 w-5" />
											</span>
											<span>Nenhuma empresa nesta situação.</span>
										</span>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>
		</MainContainer>
	);
};

export default Companies;
